import { useCallback, useEffect, useRef, useState } from 'react';

import { arrayIsEqual } from '@xemplo/common-util';
import {
  findItemsByValues,
  ListValue,
  StatefulList,
  ValueListItem,
} from '@xemplo/stateful-list-core';

type UseValueTrackerProps = {
  value: ListValue[] | null;
  onChange?: (value: ListValue[]) => void;
  items: StatefulList;
};

/**
 * A custom React hook that manages and tracks selected values in a multi-select component.
 *
 * This hook maintains an internal state of selected values and their corresponding items,
 * with a persistent cache that survives list filtering. It provides methods to modify
 * the selection and automatically syncs with external value changes.
 *
 * The idea is to keep a single source of truth for selected values while allowing
 * efficient updates and retrieval of selected items.
 */
export function useValueTracker({ value, onChange, items }: UseValueTrackerProps) {
  const [internalValue, setInternalValue] = useState<ListValue[]>(value ?? []);

  // Persistent cache of selected items - survives list filtering
  const selectedItemsCache = useRef<Map<ListValue, ValueListItem>>(new Map());

  const [valueListItems, setValueListItems] = useState<ValueListItem[]>(() => {
    const foundItems = findItemsByValues(items, internalValue);
    // Initialize cache with found items
    foundItems.forEach((item) => selectedItemsCache.current.set(item.value, item));
    return foundItems;
  });

  const cleanOrphanedEntries = useCallback((values: ListValue[]) => {
    const valueSet = new Set(values);
    for (const cachedValue of selectedItemsCache.current.keys()) {
      if (!valueSet.has(cachedValue)) {
        selectedItemsCache.current.delete(cachedValue);
      }
    }
  }, []);

  const syncValues = useCallback(
    (values: ListValue[] | null) => {
      setInternalValue((currentValue) => {
        if (values == null || values.length === 0) {
          setValueListItems([]);
          selectedItemsCache.current.clear();
          return [];
        }

        // Always resolve items from the current list — required for async item loading:
        // when items arrive after the value is set, arrayIsEqual(values, currentValue)
        // would short-circuit before findItemsByValues runs with the loaded items.
        const foundItems = findItemsByValues(items, values);
        foundItems.forEach((item) => selectedItemsCache.current.set(item.value, item));

        // Clean orphaned cache entries (items not in current values)
        cleanOrphanedEntries(values);

        const cachedItems = values
          .map((val) => selectedItemsCache.current.get(val))
          .filter((item): item is ValueListItem => item !== undefined);

        setValueListItems(cachedItems);

        // Return the same reference when values haven't changed to avoid a
        // downstream re-render from internalValue changing identity.
        return arrayIsEqual(values, currentValue) ? currentValue : values;
      });
    },
    [items, cleanOrphanedEntries],
  );

  const handleChange = useCallback(
    (selected: ListValue[]) => {
      syncValues(selected);
      onChange?.(selected);
    },
    [onChange, syncValues],
  );

  const removeItem = useCallback(
    (item: ValueListItem) => {
      const newValues = internalValue.filter((val) => val !== item.value);
      // Remove from cache when explicitly removed
      selectedItemsCache.current.delete(item.value);
      handleChange(newValues);
    },
    [handleChange, internalValue],
  );

  const clear = useCallback(() => {
    // Clear cache when all items removed
    selectedItemsCache.current.clear();
    handleChange([]);
  }, [handleChange]);

  // Sync when external value prop changes
  useEffect(() => {
    syncValues(value);
  }, [syncValues, value]);

  return {
    /** The current array of selected values */
    internalValue,

    /** The current array of selected items with full metadata */
    valueListItems,

    /** Boolean indicating whether any items are selected */
    hasValue: valueListItems.length > 0,

    /** Function to update the selection with new values */
    handleChange,

    /** Function to remove a specific item from the selection */
    removeItem,

    /** Function to clear all selected items */
    clear,
  };
}
